/**
 * Policy record — Premium tab (OR-1667, design OR-1639 "Premium").
 *
 * Reads `GET /policy-terms/{id}/premium` (backend OR-1625): the issued
 * transactions, newest first. Each row reads "<Type> · <endorsement
 * number>", the effective date below, and the signed premium on the right. The arrow follows the design note: down is money in, up is
 * money out, like Wise.
 *
 * States from the design:
 *  - bound, not issued yet → the premium is recorded on a draft issuance
 *    and shows up once issued; no link, editing the submission wouldn't
 *    change the policy (OR-1800)
 *  - bound with no premium recorded → explains it and links to the
 *    submission's Premium Selection (new tab) to fix it there
 *  - the request failed → "Couldn't load premium breakdown" with Try again
 *  - search with no match → the shared "No Results Found"
 *
 * Out of scope for the MVP build: the transaction detail panel (its flow
 * isn't defined) and the per-facility breakdown list.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, CurrencyDollarCircle, LinkExternal01, RefreshCw05 } from "@untitledui/icons";
import { Button, cx } from "@tricura/atlas-ui";
import { ListEmptyState } from "../../components/list-states";
import { Skeleton } from "../../components/skeleton";
import { formatDate, moneyCompact } from "./format";
import { type PolicyTransactionType, type PremiumTransaction, getTermPremium } from "../../api";
import { TabState } from "./tab-state";

const TYPE_LABELS: Record<PolicyTransactionType, string> = {
    // The term's first transaction, "New business" in the design.
    issuance: "New business",
    endorsement: "Endorsement",
    renewal: "Renewal",
    extension: "Extension",
    cancellation: "Cancellation",
    reinstatement: "Reinstatement",
    correction: "Correction",
};

/** "+ $3M" / "- $500K", coloured by direction. */
function SignedAmount({ amount }: { amount: string }) {
    const n = Number(amount);
    const out = n < 0;
    return (
        <span className={cx("text-sm font-medium tabular-nums", out ? "text-error-primary" : "text-success-primary")}>
            {out ? "- " : "+ "}
            {moneyCompact(String(Math.abs(n)))}
        </span>
    );
}

type Load = { status: "loading" } | { status: "ready"; rows: PremiumTransaction[]; pendingIssuance: boolean } | { status: "error" };

interface PremiumTabProps {
    termId: string;
    search: string;
    /** Opens the submission's Premium Selection; null hides the link. */
    submissionHref: string | null;
}

export function PremiumTab({ termId, search, submissionHref }: PremiumTabProps) {
    const [load, setLoad] = useState<Load>({ status: "loading" });
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoad({ status: "loading" });
        getTermPremium(termId)
            .then(({ rows, pendingIssuance }) => !cancelled && setLoad({ status: "ready", rows, pendingIssuance }))
            .catch(() => !cancelled && setLoad({ status: "error" }));
        return () => {
            cancelled = true;
        };
    }, [termId, reloadKey]);

    const all = useMemo(() => (load.status === "ready" ? load.rows : []), [load]);
    const rows = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return all;
        return all.filter((t) => `${TYPE_LABELS[t.type]} ${t.summary}`.toLowerCase().includes(term));
    }, [all, search]);

    if (load.status === "error") {
        return (
            <TabState
                icon={CurrencyDollarCircle}
                title="Couldn't load premium breakdown"
                description={
                    <>
                        We had some trouble loading the premium
                        <br />
                        breakdown for this policy
                    </>
                }
                data-autotest="policy-premium-state-error"
                action={
                    <Button color="secondary" size="sm" iconTrailing={RefreshCw05} onClick={() => setReloadKey((k) => k + 1)}>
                        Try again
                    </Button>
                }
            />
        );
    }

    if (load.status === "ready" && all.length === 0 && load.pendingIssuance) {
        return (
            <TabState
                icon={CurrencyDollarCircle}
                title="Premium pending issuance"
                description="The bound premium appears once the policy is issued"
                data-autotest="policy-premium-state-pending"
            />
        );
    }

    if (load.status === "ready" && all.length === 0) {
        return (
            <TabState
                icon={CurrencyDollarCircle}
                title="No premium breakdown found"
                description="This policy is bound, but no premium was ever recorded on the submission. Open it to add the missing value."
                data-autotest="policy-premium-state-empty"
                action={
                    submissionHref && (
                        <Button color="secondary" size="sm" iconTrailing={LinkExternal01} href={submissionHref} target="_blank" rel="noopener noreferrer">
                            View Submission
                        </Button>
                    )
                }
            />
        );
    }

    if (load.status === "ready" && rows.length === 0) {
        return <ListEmptyState description="Try a different search" />;
    }

    return (
        <div className="atlas-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4 md:px-5" data-autotest="policy-premium-list">
            <h3 className="text-primary pt-4 pb-2 text-sm font-semibold">Transactions</h3>
            <ul className="divide-secondary flex flex-col divide-y">
                {load.status === "loading"
                    ? Array.from({ length: 6 }, (_, i) => (
                          <li key={i} className="flex items-center gap-3 py-3">
                              <Skeleton className="size-8 rounded-full" />
                              <div className="flex flex-1 flex-col gap-1.5">
                                  <Skeleton className="h-4 w-56" />
                                  <Skeleton className="h-3 w-20" />
                              </div>
                              <Skeleton className="h-4 w-16" />
                          </li>
                      ))
                    : rows.map((t) => {
                          const Arrow = Number(t.amount) < 0 ? ArrowUp : ArrowDown;
                          return (
                              <li key={t.id} className="flex items-center gap-3 py-3" data-autotest="policy-premium-row">
                                  <span className="bg-secondary flex size-8 shrink-0 items-center justify-center rounded-full">
                                      <Arrow aria-hidden="true" className="text-fg-quaternary size-4" />
                                  </span>
                                  <div className="flex min-w-0 flex-1 flex-col">
                                      <span className="truncate text-sm">
                                          <span className="text-primary font-medium">{TYPE_LABELS[t.type]}</span>
                                          <span className="text-tertiary"> · {t.summary}</span>
                                      </span>
                                      <span className="text-tertiary text-xs tabular-nums">{formatDate(t.effectiveDate)}</span>
                                  </div>
                                  <SignedAmount amount={t.amount} />
                              </li>
                          );
                      })}
            </ul>
        </div>
    );
}
